import { action, makeObservable, observable, reaction, runInAction } from "mobx";
import type { IReactionDisposer } from "mobx";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { CoreStore } from "Stores/RESTStore/CoreStore";
import { userInfoStore } from "Stores/UserInfoStore";

export type FeatureFlagsSnapshot = Record<string, API_FeatureFlagEvaluationDTO>;

const EMPTY_SNAPSHOT: FeatureFlagsSnapshot = {};

class FeatureFlagsStore extends CoreStore {
  userFlags: FeatureFlagsSnapshot = EMPTY_SNAPSHOT;
  projectFlags: FeatureFlagsSnapshot = EMPTY_SNAPSHOT;
  loadedProjectId: number = null;
  private requestedProjectId: number = null;

  constructor() {
    super();

    makeObservable(this, {
      userFlags: observable.ref,
      projectFlags: observable.ref,
      loadedProjectId: observable,
      fetchMe: action.bound,
      fetchForProject: action.bound,
      clear: action,
    });
  }

  clear() {
    this.projectFlags = EMPTY_SNAPSHOT;
    this.loadedProjectId = null;
    this.requestedProjectId = null;
  }

  url(projectId?: number): string {
    return projectId ? API_URL.FEATURE_FLAGS.getProjectFeatures(projectId) : API_URL.FEATURE_FLAGS.getMyFeatures();
  }

  startSessionSync(): IReactionDisposer {
    return reaction(
      () => this.canEvaluate,
      (canEvaluate) => {
        if (canEvaluate) {
          this.fetchMe();
          return;
        }
        runInAction(() => {
          this.userFlags = EMPTY_SNAPSHOT;
        });
      },
      { fireImmediately: true }
    );
  }

  async fetchMe(): Promise<void> {
    if (!this.canEvaluate) {
      return;
    }

    const snapshot = await this.fetchSnapshot(this.url());
    runInAction(() => {
      this.userFlags = snapshot;
    });
  }

  async fetchForProject(projectId: number): Promise<void> {
    if (!this.canEvaluate || !projectId || this.loadedProjectId === projectId) {
      return;
    }

    this.requestedProjectId = projectId;
    const snapshot = await this.fetchSnapshot(this.url(projectId));
    if (this.requestedProjectId !== projectId) {
      return;
    }

    runInAction(() => {
      this.projectFlags = snapshot;
      this.loadedProjectId = projectId;
    });
  }

  isEnabled(flagKey: string): boolean {
    const snapshot = this.loadedProjectId ? this.projectFlags : this.userFlags;
    return snapshot[flagKey]?.enabled === true;
  }

  private get canEvaluate(): boolean {
    return userInfoStore.isAuthenticated && !userInfoStore.isGuest;
  }

  private async fetchSnapshot(url: string): Promise<FeatureFlagsSnapshot> {
    runInAction(() => {
      this.state = "LOADING";
    });

    try {
      const snapshot = await API.getData<FeatureFlagsSnapshot>(url);
      runInAction(() => {
        this.state = "DONE";
      });
      return snapshot || EMPTY_SNAPSHOT;
    } catch (error) {
      runInAction(() => {
        this.state = "ERROR";
      });
      console.error("Failed to evaluate feature flags:", url, error);
      return EMPTY_SNAPSHOT;
    }
  }
}

export const featureFlagsStore = new FeatureFlagsStore();
export { FeatureFlagsStore };
